import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit } from "@angular/core";
import { MAT_BOTTOM_SHEET_DATA, MatBottomSheetRef } from "@angular/material/bottom-sheet";
import { DataroomNode } from "@core/models/dataroom-node.model";
import { Observable, tap } from "rxjs";
import { MediaType } from "@core/attachment/media-type.typings";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import { getIcon, getIconColor } from '@core/attachment/helpers';
import { TranslateService } from "@ngx-translate/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { DownloaderService } from "@core/downloader/downloader.service";
import { TypedFormGroup } from '@feature/forms/form-model';

export interface BulkDownloadBottomSheetData {
  nodes: DataroomNode[];
}

interface NodeItem {
  name: string;
  size: number;
  url: string;
  type: string;
  isDownloaded: boolean;
  isDownloading: boolean;
  node: DataroomNode;
  error: string;
}

@UntilDestroy()
@Component({
    selector: 'wai-bulk-download-bottom-sheet',
    templateUrl: './bulk-download-bottom-sheet.component.html',
    styleUrls: ['./bulk-download-bottom-sheet.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class BulkDownloadBottomSheetComponent implements OnInit {
  public asTypedFormGroup = (x: unknown): TypedFormGroup<any> => x as TypedFormGroup<any>;

  isDownloading = false;
  nodes: NodeItem[] = [];
  displayedColumns: string[] = ['name', 'size', 'actions'];
  error: string = null;

  constructor(
    @Inject(MAT_BOTTOM_SHEET_DATA) public bottomSheetData: BulkDownloadBottomSheetData,
    private bottomSheetRef: MatBottomSheetRef<BulkDownloadBottomSheetComponent>,
    private downloader: DownloaderService,
    private cdr: ChangeDetectorRef,
    private translateService: TranslateService,
  ) {
    this.initNodes();
  }

  ngOnInit(): void {
    this.downloadAll();
  }

  continue(): void {
    this.downloadAll();
  }

  downloadAll(): void {
    this.isDownloading = true;
    this.error = null;
    this.cdr.detectChanges();
    this.download()
      .pipe(
        tap((hasMore) => {
          if (!hasMore) {
            this.isDownloading = false;
          } else {
            this.downloadAll();
          }
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  download(): Observable<boolean> {
    return new Observable((observer) => {
      const nodeItem = this.nodes.find(node => !node.isDownloaded && !node.isDownloading && !node.error);

      if (!nodeItem) {
        observer.next(false);
        observer.complete();
        return;
      }

      nodeItem.isDownloading = true;
      this.cdr.detectChanges();
      this.downloader.downloadAndSave(nodeItem.node?.file?.url, nodeItem.node?.name)
        .pipe(
          tap((result) => {
            nodeItem.isDownloaded = result;
            nodeItem.isDownloading = false;
            this.error = result ? null : this.translateService.instant('INTL_FAILED_FILE_SAVE_BLOCKED_POPUPS');
            this.cdr.detectChanges();
            observer.next(result);
            observer.complete();
          }),
          untilDestroyed(this)
        )
        .subscribe();
    });
  }

  close() {
    this.bottomSheetRef.dismiss();
  }

  public getIcon(type: MediaType): IconDefinition {
    return getIcon(type);
  }

  public getIconColor(type: MediaType): string {
    return getIconColor(type);
  }

  private initNodes(): void {
    this.nodes = (this.bottomSheetData.nodes || []).map(node => ({
      name: node.name,
      size: node.file?.fileSize,
      url: node.file?.url,
      type: node.file?.type,
      isDownloaded: false,
      isDownloading: false,
      node,
      error: !!node?.file ? null : 'File object is missing.',
    }));
  }
}
